import type { SyntheticEvent } from 'react'
import { useCallback, useState } from 'react'

import { VaultMediaType } from '@api'
import { useUnit } from 'effector-react'
import OutsideClickHandler from 'react-outside-click-handler'

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.tsx'
import {
  $canLoadMoreVaultCategoryPages,
  $selectedCategory,
  $vaultCategories,
  $vaultCategoriesError,
  getNextVaultCategoriesPageFx,
  setSelectedCategory,
} from '@/models/vault/categories.ts'
import { getVaultMediasFx } from '@/models/vault/medias'

export const VaultCategorySelect = () => {
  const [open, setOpen] = useState(false)
  const vaultCategories = useUnit($vaultCategories)
  const isCategoriesLoading = useUnit(getNextVaultCategoriesPageFx.pending)
  const canLoadMore = useUnit($canLoadMoreVaultCategoryPages)
  const error = useUnit($vaultCategoriesError)

  const handleScroll = (event: SyntheticEvent<HTMLDivElement>) => {
    const target = event.target as HTMLDivElement
    const distanceToEnd =
      target.scrollHeight - target.clientHeight - target.scrollTop

    // fetch next items when only one item left
    if (distanceToEnd < 60 && !isCategoriesLoading && canLoadMore && !error) {
      getNextVaultCategoriesPageFx()
    }
  }
  const selectedCategory = useUnit($selectedCategory)
  const isLoadingMedia = useUnit(getVaultMediasFx.pending)

  const handleChange = useCallback(
    (typeOrId: string) => {
      if (typeOrId === VaultMediaType.ALL) {
        setSelectedCategory(null)
      } else {
        const cat = vaultCategories.find((c) => c.id.toString() === typeOrId)!

        setSelectedCategory(cat)
      }
    },
    [vaultCategories],
  )

  return (
    <Select
      value={selectedCategory?.id?.toString()}
      onValueChange={handleChange}
      open={open}
      onOpenChange={setOpen}
      disabled={isCategoriesLoading || isLoadingMedia}
    >
      <SelectTrigger className="text-title w-full">
        {selectedCategory ? <SelectValue /> : 'All Media'}
      </SelectTrigger>
      {/* this is a hack to prevent Radix select outside click bug on mobile view */}
      <OutsideClickHandler onOutsideClick={() => setOpen(false)}>
        <SelectContent
          // this is a hack to prevent Radix select touch bug

          ref={(ref) =>
            ref?.addEventListener('touchend', (event) => event.preventDefault())
          }
        >
          <SelectGroup>
            <div
              className="z-100 max-h-[200px] overflow-y-scroll"
              onScroll={handleScroll}
            >
              <SelectItem value={VaultMediaType.ALL}>All Media</SelectItem>
              {vaultCategories.map((c) => (
                <SelectItem
                  key={c.id}
                  value={c.id.toString()}
                >
                  {c.name}{' '}
                  <span className="text-text-sub">
                    (
                    {c.audiosCount +
                      c.gifsCount +
                      c.videosCount +
                      c.photosCount}
                    )
                  </span>
                </SelectItem>
              ))}
            </div>
          </SelectGroup>
        </SelectContent>
      </OutsideClickHandler>
    </Select>
  )
}
